import { Link } from "react-router-dom";
import { useState } from "react";
import { Facebook, Twitter, Instagram, Linkedin, Mail, Phone, MapPin } from "lucide-react";
import { Logo } from "./Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Footer({ settings }) {
  const [email, setEmail] = useState("");
  const s = settings || {};

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/newsletter", { email });
      toast.success("Thank you for subscribing.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Please try again.");
    }
  };

  const openCookie = () => window.dispatchEvent(new Event("open-cookie-settings"));

  return (
    <footer className="bg-[#111827] text-white/80" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <Logo light className="text-2xl" />
            <p className="mt-4 text-sm leading-relaxed max-w-sm">
              {s.footer_description ||
                "Homio is an independent property referral platform, showcasing homes from trusted estate agents, letting agencies and developers across the UK."}
            </p>
            <form onSubmit={subscribe} className="mt-6 flex gap-2 max-w-sm" data-testid="newsletter-form">
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                data-testid="newsletter-email-input"
              />
              <Button type="submit" className="bg-[#C81D35] hover:bg-[#a5162b] shrink-0" data-testid="newsletter-submit">
                Subscribe
              </Button>
            </form>
          </div>

          <FooterCol title="Properties" links={[
            ["All Properties", "/properties"],
            ["For Sale", "/properties/for-sale"],
            ["To Let", "/properties/to-let"],
            ["Featured", "/featured"],
          ]} />
          <FooterCol title="Company" links={[
            ["About", "/about"],
            ["How It Works", "/how-it-works"],
            ["Partner With Us", "/partner"],
            ["Agencies", "/agencies"],
            ["Blog", "/blog"],
            ["Contact", "/contact"],
          ]} />
          <FooterCol title="Legal" links={[
            ["Privacy Policy", "/privacy-policy"],
            ["Terms & Conditions", "/terms"],
            ["Cookie Policy", "/cookie-policy"],
            ["Disclaimer", "/disclaimer"],
            ["Referral Information", "/referral-information"],
            ["Complaints", "/complaints"],
          ]} />
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 grid gap-6 md:grid-cols-3 text-sm">
          <div className="space-y-2">
            {s.contact_phone && <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#C5A059]" /> {s.contact_phone}</p>}
            {s.contact_email && <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#C5A059]" /> {s.contact_email}</p>}
            {s.business_address && <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#C5A059]" /> {s.business_address}</p>}
          </div>
          <div className="flex md:justify-center items-start gap-3">
            {[[Facebook, s.social?.facebook], [Twitter, s.social?.twitter], [Instagram, s.social?.instagram], [Linkedin, s.social?.linkedin]].map(
              ([Icon, url], i) =>
                url ? (
                  <a key={i} href={url} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#C81D35] flex items-center justify-center transition-colors" data-testid={`social-${i}`}>
                    <Icon className="w-4 h-4 text-white" />
                  </a>
                ) : null
            )}
          </div>
          <div className="md:text-right">
            <button onClick={openCookie} className="text-white/70 hover:text-white underline" data-testid="footer-cookie-settings">
              Cookie settings
            </button>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-white/10 text-xs text-white/50 space-y-2">
          <p className="max-w-4xl">
            <strong className="text-white/70">Referral platform disclaimer:</strong> Homio acts as an introducer and referral platform. Properties are supplied by third-party
            agencies and partners who are responsible for direct customer communication and transactions. Property details should be independently verified. Homio may receive a
            referral fee. Nothing on this website constitutes legal, financial or property advice.
          </p>
          <p>{s.copyright_text || "© 2026 Homio. All rights reserved."}</p>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <h4 className="text-white font-semibold text-sm mb-4">{title}</h4>
      <ul className="space-y-2.5">
        {links.map(([label, to]) => (
          <li key={to}>
            <Link to={to} className="text-sm hover:text-white transition-colors">
              {label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
